import React, { useRef, useEffect, useState } from 'react'
import {
  Brain,
  CheckCircle2,
  AlertCircle,
  FileText,
  Search,
  Loader2,
  ArrowDown,
  Sparkles,
} from 'lucide-react'
import { useCase } from '../../../../hooks/useCase'
import { useActivityFeed, type ActivityEvent, type ActivityEventType, type ActivityEventIcon } from '../../../../hooks/useActivityFeed'
import styles from './AgentStreamPanel.module.css'

interface ActivityFeedPanelProps {
  caseId: string
  maxHeight?: string
  showHeader?: boolean
}

/**
 * Get icon for activity event
 */
function getActivityIcon(icon: ActivityEventIcon, type: ActivityEventType) {
  switch (icon) {
    case 'search':
      return <Search size={16} className={styles.actionIcon} />
    case 'file':
      return <FileText size={16} className={styles.documentIcon} />
    case 'check':
      return <CheckCircle2 size={16} className={styles.resultIcon} />
    case 'alert':
      return <AlertCircle size={16} className={styles.errorIcon} />
    case 'sparkles':
    default:
      // Different colors based on event type
      if (type === 'thinking') {
        return <Brain size={16} className={styles.thinkingIcon} />
      }
      if (type === 'progress') {
        return <Loader2 size={16} className={`${styles.progressIcon} animate-spin`} />
      }
      return <Sparkles size={16} className={styles.defaultIcon} />
  }
}

/**
 * Get human-readable label for event type
 */
function getEventTypeLabel(type: ActivityEventType): string {
  switch (type) {
    case 'thinking':
      return 'Analyzing'
    case 'action':
      return 'Action'
    case 'discovery':
      return 'Discovery'
    case 'progress':
      return 'Progress'
    case 'complete':
      return 'Complete'
    case 'error':
      return 'Error'
    default:
      return 'Event'
  }
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now()
  const eventTime = new Date(timestamp).getTime()
  const diff = now - eventTime

  if (diff < 1000) return 'just now'
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  return new Date(timestamp).toLocaleTimeString()
}

/**
 * Single activity event item component
 */
const ActivityEventItem: React.FC<{ event: ActivityEvent }> = ({ event }) => {
  return (
    <div className={`${styles.eventItem} ${styles[`event_${event.type}`]}`}>
      <div className={styles.eventHeader}>
        <div className={styles.eventIconWrapper}>
          {getActivityIcon(event.icon, event.type)}
        </div>
        <div className={styles.eventContent}>
          <div className={styles.eventMessage}>{event.message}</div>
          {event.detail && (
            <div className={styles.eventDetail}>{event.detail}</div>
          )}
          <div className={styles.eventMeta}>
            <span className={styles.eventType}>{getEventTypeLabel(event.type)}</span>
            <span className={styles.metaSeparator}>·</span>
            <span className={styles.eventTime}>{formatRelativeTime(event.timestamp)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * ActivityFeedPanel - Displays real-time activity events from Firestore subcollection
 *
 * This component subscribes to the cases/{caseId}/activity subcollection
 * for real-time streaming of agent activity events.
 */
const ActivityFeedPanel: React.FC<ActivityFeedPanelProps> = ({
  caseId,
  maxHeight = '400px',
  showHeader = true,
}) => {
  // Get case data for status info
  const { caseData } = useCase(caseId)
  const isScanning = caseData?.documentScan?.status === 'scanning'
  const statusMessage = caseData?.documentScan?.statusMessage

  // Subscribe to activity subcollection
  const { events: activityFeed, loading } = useActivityFeed(caseId)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (autoScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [activityFeed, autoScroll])

  // Handle scroll to detect if user scrolled up
  const handleScroll = () => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50

    setAutoScroll(isAtBottom)
    setShowScrollButton(!isAtBottom && activityFeed.length > 5)
  }

  // Scroll to bottom when button clicked
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
      setAutoScroll(true)
    }
  }

  if (loading) {
    return (
      <div className={styles.container}>
        {showHeader && (
          <div className={styles.header}>
            <Sparkles size={18} />
            <span>Activity Feed</span>
          </div>
        )}
        <div className={styles.emptyState}>
          <Loader2 size={32} className={`${styles.emptyIcon} animate-spin`} />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (activityFeed.length === 0 && !isScanning) {
    return (
      <div className={styles.container}>
        {showHeader && (
          <div className={styles.header}>
            <Sparkles size={18} />
            <span>Activity Feed</span>
          </div>
        )}
        <div className={styles.emptyState}>
          <Brain size={32} className={styles.emptyIcon} />
          <p>No activity yet</p>
          <span>Events will appear here when document scanning starts</span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {showHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Sparkles size={18} />
            <span>Activity Feed</span>
            {isScanning && (
              <span className={styles.liveIndicator}>
                <span className={styles.liveDot} />
                Live
              </span>
            )}
          </div>
          <div className={styles.headerRight}>
            <span className={styles.eventCount}>{activityFeed.length} events</span>
          </div>
        </div>
      )}

      {/* Current Status Banner */}
      {isScanning && statusMessage && (
        <div className={styles.currentActionBanner}>
          <Loader2 size={16} className="animate-spin" />
          <span className={styles.currentActionText}>{statusMessage}</span>
        </div>
      )}

      {/* Events List */}
      <div
        ref={scrollContainerRef}
        className={styles.eventsContainer}
        style={{ maxHeight }}
        onScroll={handleScroll}
      >
        {activityFeed.map((event) => (
          <ActivityEventItem key={event.id} event={event} />
        ))}

        {/* Processing indicator at bottom */}
        {isScanning && (
          <div className={styles.processingIndicator}>
            <Loader2 size={14} className="animate-spin" />
            <span>Processing...</span>
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button className={styles.scrollToBottomButton} onClick={scrollToBottom}>
          <ArrowDown size={16} />
          <span>Latest</span>
        </button>
      )}
    </div>
  )
}

export default ActivityFeedPanel
